import { useState, useCallback } from 'react';
import type { PviGraphWithSpans } from '../../editor/pviTypes';
import { exportPviJson } from '../../editor/pviGraphService';
import styles from './PviGraphPanel.module.css';

interface JsonViewProps {
  graph: PviGraphWithSpans;
}

export function JsonView({ graph }: JsonViewProps) {
  const [expanded, setExpanded] = useState(true);
  const [copied, setCopied] = useState(false);

  const json = exportPviJson(graph);
  const jsonStr = JSON.stringify(json, null, 2);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(jsonStr);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = jsonStr;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [jsonStr]);

  if (graph.nodes.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>No PVI graph to display</p>
      </div>
    );
  }

  return (
    <div className={styles.jsonContainer}>
      <div className={styles.jsonToolbar}>
        <button className={styles.jsonBtn} onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Collapse' : 'Expand'}
        </button>
        <button className={styles.jsonBtn} onClick={handleCopy}>
          {copied ? '✓ Copied' : 'Copy JSON'}
        </button>
        <span className={styles.jsonMeta}>
          {graph.nodes.length} node{graph.nodes.length !== 1 ? 's' : ''}, {graph.edges.length} edge{graph.edges.length !== 1 ? 's' : ''}
        </span>
      </div>
      <pre className={styles.jsonPre}>
        <code>{expanded ? jsonStr : JSON.stringify(json)}</code>
      </pre>
    </div>
  );
}
